import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import {
  User,
  Mail,
  School,
  Book,
  Clock,
  Bell,
  Save,
  ArrowLeft,
  Award,
  AlertCircle,
} from "lucide-react";
import "./ProfileEdit.css";

const ProfileEdit = () => {
  console.log("ProfileEdit component is loading...");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // "idle" | "checking" | "free" | "taken" | "invalid", with a message for the person
  const [usernameCheck, setUsernameCheck] = useState({ state: "idle", message: "" });
  const savedUsername = useRef("");
  const [profileData, setProfileData] = useState({
    name: "",
    surname: "",
    username: "",
    email: "",
    grade: "",
    school: "",
    preferredTime: "",
    learningStyle: "",
    notifications: "Email + Push",
    bio: "",
    interests: [],
    goals: "",
    term: "", // First, Second, Third
    difficultyLevel: "intermediate", // beginner, intermediate, advanced
    studyReminders: true,
  });

  // Fetch profile data on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Debug authentication state
        console.log("=== Authentication Debug ===");
        console.log("Role (localStorage):", localStorage.getItem("role"));
        console.log("Role (sessionStorage):", sessionStorage.getItem("role"));
        console.log(
          "Student ID (localStorage):",
          localStorage.getItem("student_id")
        );
        console.log(
          "Student ID (sessionStorage):",
          sessionStorage.getItem("student_id")
        );
        console.log("Username:", localStorage.getItem("username"));
        console.log(
          "Access Token (localStorage):",
          !!localStorage.getItem("access_token")
        );
        console.log(
          "Access Token (sessionStorage):",
          !!sessionStorage.getItem("access_token")
        );

        // Check user role and ID from both storage locations
        const userRole =
          localStorage.getItem("role") || sessionStorage.getItem("role");
        const studentId =
          localStorage.getItem("student_id") ||
          sessionStorage.getItem("student_id");
        const teacherId =
          localStorage.getItem("teacher_id") ||
          sessionStorage.getItem("teacher_id");

        console.log("=== Validation Check ===");
        console.log("UserRole (combined):", userRole);
        console.log("StudentId (combined):", studentId);
        console.log("TeacherId (combined):", teacherId);
        console.log("Overall check:", !userRole || (!studentId && !teacherId));

        // Since ProtectedRoute already validated the user and we have student_id, proceed
        // The user is already authenticated if they reached this component
        console.log("=== Proceeding with API call ===");
        console.log("Will use student_id:", studentId);
        console.log("User appears to be authenticated via ProtectedRoute");

        // Use the /me endpoint which we know works correctly
        console.log("Attempting to fetch profile data...");
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");
        console.log("Token available:", !!token);
        console.log(
          "Token preview:",
          token ? token.substring(0, 20) + "..." : "No token"
        );

        // Test direct fetch first to see raw response
        try {
          const testResponse = await fetch("http://localhost:8000/profile/me", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          console.log("Direct fetch status:", testResponse.status);
          console.log("Direct fetch ok:", testResponse.ok);
          const testData = await testResponse.text();
          console.log("Direct fetch response:", testData);
        } catch (testError) {
          console.error("Direct fetch error:", testError);
        }

        const response = (await api.get("/profile/me")).data;
        console.log("Profile API response:", response);

        // Set profile data from response
        if (response) {
          // Convert snake_case to camelCase
          const transformedData = {};
          Object.entries(response).forEach(([key, value]) => {
            const camelKey = key.replace(/_([a-z])/g, (g) =>
              g[1].toUpperCase()
            );
            transformedData[camelKey] = value;
          });

          savedUsername.current = transformedData.username || "";
          setProfileData((prev) => ({
            ...prev,
            ...transformedData,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch profile data:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        console.error("Error message:", error.message);

        let errorMessage = "Failed to fetch profile data. Please try again.";
        if (error.response?.status === 401) {
          errorMessage = "Authentication failed. Please login again.";
          // Clear all auth data and redirect to login
          localStorage.clear();
          sessionStorage.clear();
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else if (error.response?.status === 404) {
          errorMessage = "Profile not found. Please contact support.";
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }

        setError(errorMessage); // Fall back to localStorage data
        const name = localStorage.getItem("name") || "";
        const surname = localStorage.getItem("surname") || "";
        const email = localStorage.getItem("email") || "";

        setProfileData((prev) => ({
          ...prev,
          name,
          surname,
          email,
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Ask the server whether the typed username is free (waits for a pause in typing)
  useEffect(() => {
    const name = (profileData.username || "").trim();
    if (!name || name === savedUsername.current) {
      setUsernameCheck({ state: "idle", message: "" });
      return undefined;
    }
    if (!/^[A-Za-z0-9_]{3,30}$/.test(name)) {
      setUsernameCheck({ state: "invalid", message: "Use 3 to 30 letters, numbers or underscores." });
      return undefined;
    }
    setUsernameCheck({ state: "checking", message: "Checking…" });
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/username-available", { params: { username: name } });
        if (!live) return;
        setUsernameCheck(data.available
          ? { state: "free", message: "Username is available" }
          : { state: "taken", message: data.reason || "That username is already taken." });
      } catch {
        if (live) setUsernameCheck({ state: "idle", message: "" });   // the save will check again
      }
    }, 450);
    return () => { live = false; clearTimeout(timer); };
  }, [profileData.username]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInterestsChange = (e) => {
    const value = e.target.value;
    // Split by commas and trim whitespace
    const interestsArray = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);
    setProfileData((prev) => ({
      ...prev,
      interests: interestsArray,
    }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate required fields
    if (!profileData.grade || profileData.grade.trim() === "") {
      setError("Please select your class level (grade).");
      setLoading(false);
      return;
    }

    if (usernameCheck.state === "taken" || usernameCheck.state === "invalid") {
      setError(usernameCheck.message);
      setLoading(false);
      return;
    }

    // Debug: Show what we're sending
    console.log("=== DEBUG: About to save profile ===");
    console.log("Selected grade:", profileData.grade);
    console.log("Full profileData:", profileData);

    try {
      // Prepare data for API - convert camelCase back to snake_case
      const apiData = {};
      Object.entries(profileData).forEach(([key, value]) => {
        const snakeKey = key.replace(
          /[A-Z]/g,
          (letter) => `_${letter.toLowerCase()}`
        );
        apiData[snakeKey] = value;
      });

      console.log("=== Save Profile Debug ===");
      console.log("Original profileData:", profileData);
      console.log("Converted apiData:", apiData);

      // Use the same endpoint for updates as we do for fetching
      const endpoint = "/profile/me";

      const response = await api.put(endpoint, apiData);

      if (response && response.data) {
        console.log("Profile updated successfully:", response.data);

        // Debug: Show what was returned
        console.log("=== DEBUG: Save response ===");
        console.log("Response grade:", response.data.grade);
        console.log("Full response:", response.data);

        // Update localStorage with all the returned profile data
        const updatedProfileData = response.data.data || response.data;
        if (updatedProfileData) {
          // Update basic profile info
          localStorage.setItem("name", updatedProfileData.name || "");
          localStorage.setItem("surname", updatedProfileData.surname || "");
          localStorage.setItem("email", updatedProfileData.email || "");
          localStorage.setItem("username", updatedProfileData.username || "");

          // Update other fields that might be used in the UI
          if (updatedProfileData.grade)
            localStorage.setItem("grade", updatedProfileData.grade);
          if (updatedProfileData.school)
            localStorage.setItem("school", updatedProfileData.school);
          if (updatedProfileData.bio)
            localStorage.setItem("bio", updatedProfileData.bio);
          if (updatedProfileData.class_level)
            localStorage.setItem("grade", updatedProfileData.class_level);
          if (updatedProfileData.term)
            localStorage.setItem("term", updatedProfileData.term);
          if (updatedProfileData.learning_style)
            localStorage.setItem(
              "learningStyle",
              updatedProfileData.learning_style
            );
          if (updatedProfileData.difficulty_level)
            localStorage.setItem(
              "difficultyLevel",
              updatedProfileData.difficulty_level
            );
          if (updatedProfileData.study_reminders !== undefined)
            localStorage.setItem(
              "studyReminders",
              updatedProfileData.study_reminders
            );

          console.log("LocalStorage updated with profile data");
        } else {
          console.warn("Profile update succeeded but no data was returned");
          // Fallback to form data
          localStorage.setItem("name", profileData.name || "");
          localStorage.setItem("surname", profileData.surname || "");
          localStorage.setItem("email", profileData.email || "");
        }

        setSuccess(true);

        // Return to dashboard after successful save
        setTimeout(() => {
          const dashboardPath =
            userRole === "student"
              ? "/student-dashboard"
              : "/teacher-dashboard";
          navigate(dashboardPath, {
            state: { profileUpdated: true, timestamp: Date.now() },
          });
        }, 1500);
      }
    } catch (error) {
      console.error("Failed to save profile:", error);
      setError("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="profile-edit-container">
        <div className="profile-edit-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1>Edit Profile</h1>
        </div>

        {error && (
          <div className="alert error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="alert success">
            <Save size={16} />
            Profile saved successfully!
          </div>
        )}

        <form className="profile-edit-form" onSubmit={saveProfile}>
          <div className="form-section">
            <h2>Personal Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <User size={16} />
                  First Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <User size={16} />
                  Last Name
                </label>
                <input
                  type="text"
                  name="surname"
                  value={profileData.surname}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <User size={16} />
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={profileData.username || ""}
                  onChange={handleInputChange}
                  maxLength={30}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  aria-invalid={usernameCheck.state === "taken" || usernameCheck.state === "invalid"}
                />
                {usernameCheck.message && (
                  <small
                    role="status"
                    style={{ color: usernameCheck.state === "free" ? "#15803d" : usernameCheck.state === "checking" ? undefined : "#b91c1c" }}
                  >
                    {usernameCheck.message}
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  required
                  disabled // Email shouldn't be changed easily
                />
                <small>Contact support to change your email address</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Academic Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <School size={16} />
                  Grade <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="grade"
                  value={profileData.grade || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Class Level</option>
                  <option value="JSS1">JSS1</option>
                  <option value="JSS2">JSS2</option>
                  <option value="JSS3">JSS3</option>
                  <option value="SS1">SS1</option>
                  <option value="SS2">SS2</option>
                  <option value="SS3">SS3</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <Book size={16} />
                  Term
                </label>
                <select
                  name="term"
                  value={profileData.term || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Term</option>
                  <option value="First">First</option>
                  <option value="Second">Second</option>
                  <option value="Third">Third</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <School size={16} />
                  School
                </label>
                <input
                  type="text"
                  name="school"
                  value={profileData.school || ""}
                  onChange={handleInputChange}
                  placeholder="Your school name"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>About Me</h2>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                name="bio"
                value={profileData.bio || ""}
                onChange={handleInputChange}
                placeholder="Tell us a bit about yourself..."
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-section">
            <h2>Learning Preferences</h2>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <Clock size={16} />
                  Preferred Learning Time
                </label>
                <select
                  name="preferredTime"
                  value={profileData.preferredTime || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Time</option>
                  <option value="Morning">Morning</option>
                  <option value="Afternoon">Afternoon</option>
                  <option value="Evening">Evening</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
              <div className="form-group">
                <label>Learning Style</label>
                <select
                  name="learningStyle"
                  value={profileData.learningStyle || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Style</option>
                  <option value="visual">Visual</option>
                  <option value="auditory">Auditory</option>
                  <option value="reading">Reading/Writing</option>
                  <option value="kinesthetic">Kinesthetic</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Difficulty Level</label>
                <select
                  name="difficultyLevel"
                  value={profileData.difficultyLevel || "intermediate"}
                  onChange={handleInputChange}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <Bell size={16} />
                  Notification Preferences
                </label>
                <select
                  name="notifications"
                  value={profileData.notifications || "Email + Push"}
                  onChange={handleInputChange}
                >
                  <option value="Email + Push">
                    Email + Push Notifications
                  </option>
                  <option value="Email Only">Email Only</option>
                  <option value="Push Only">Push Notifications Only</option>
                  <option value="None">No Notifications</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="studyReminders"
                  checked={profileData.studyReminders || false}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      studyReminders: e.target.checked,
                    }))
                  }
                />
                <span>Enable study reminders and notifications</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h2>Learning Goals</h2>
            <div className="form-group">
              <label>Interests (comma separated)</label>
              <input
                type="text"
                name="interestsRaw"
                value={
                  profileData.interests ? profileData.interests.join(", ") : ""
                }
                onChange={handleInterestsChange}
                placeholder="Math, Science, Programming, etc."
              />
            </div>
            <div className="form-group">
              <label>
                <Award size={16} />
                Learning Goals
              </label>
              <textarea
                name="goals"
                value={profileData.goals || ""}
                onChange={handleInputChange}
                placeholder="What do you want to achieve with your learning?"
                rows="3"
              ></textarea>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default ProfileEdit;
